import React, { useState, useEffect, useMemo } from "react";
import { Modal, Button, Form, Alert, InputGroup } from "react-bootstrap";
import { FaPlus, FaTrash } from "react-icons/fa";

const EXTRA_EMAIL_FIELDS = ["email2", "email3", "email4", "email5"];



export default function EmailInvoiceModal({
  show,
  onHide,
  defaultEmail,
  email2,
  email3,
  email4,
  email5,
  onSend,
}) {
  const [email, setEmail] = useState("");
  const [extraEmails, setExtraEmails] = useState({
    email2: undefined,
    email3: undefined,
    email4: undefined,
    email5: undefined,
  });

  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const defaultsMap = useMemo(
    () => ({
      email2: (email2 || "").trim(),
      email3: (email3 || "").trim(),
      email4: (email4 || "").trim(),
      email5: (email5 || "").trim(),
    }),
    [email2, email3, email4, email5]
  );

  // Reset local state whenever the modal opens
  useEffect(() => {
    if (!show) return;

    setEmail((defaultEmail || "").trim());
    setError("");
    setSending(false);

    // Show all extra fields that already have values
    setExtraEmails({
      email2: defaultsMap.email2 ? defaultsMap.email2 : undefined,
      email3: defaultsMap.email3 ? defaultsMap.email3 : undefined,
      email4: defaultsMap.email4 ? defaultsMap.email4 : undefined,
      email5: defaultsMap.email5 ? defaultsMap.email5 : undefined,
    });
  }, [show, defaultEmail, defaultsMap]);

  const allExtraUsed = EXTRA_EMAIL_FIELDS.every((f) => extraEmails[f] !== undefined);

  const handleAddExtraField = () => {
    const nextField = EXTRA_EMAIL_FIELDS.find((f) => extraEmails[f] === undefined);
    if (!nextField) return;

    // Show blank (not saved; just for sending)
    setExtraEmails((prev) => ({ ...prev, [nextField]: "" }));
  };

  const handleRemoveExtraField = (field) => {
    setExtraEmails((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleExtraChange = (field, value) => {
    setExtraEmails((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
  if (!email && !email2 && !email3 && !email4 && !email5) {
    setError("At least one email is required");
    return;
  }

  onHide();
  setSending(true);
  setError("");

  onSend({
    email: (email || "").trim(),
    email2: (extraEmails.email2 || "").trim(),
    email3: (extraEmails.email3 || "").trim(),
    email4: (extraEmails.email4 || "").trim(),
    email5: (extraEmails.email5 || "").trim(),
    type: "invoice",
  })
    .catch((err) => {
      console.error("Failed to send invoice:", err);
      setError("Failed to send invoice");
    })
    .finally(() => setSending(false));
};


  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header
        closeButton
        style={{ background: "#CFE2FF" }}
        className="rounded-0 text-primary d-flex align-items-center"
      >
        <Modal.Title as="h6" className="m-0">
          Send Invoice
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        <Form>
          {/* Primary Email */}
          <Form.Group controlId="invoiceEmail">
            <Form.Label>Email</Form.Label>
            <InputGroup className="align-items-center">
              <Form.Control
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Recipient email"
                isInvalid={!!error && !(email || "").trim()}
              />

              <Button
                variant="link"
                className="text-primary d-flex align-items-center"
                onClick={handleAddExtraField}
                disabled={allExtraUsed}
                title={allExtraUsed ? "Maximum emails added" : "Add another email"}
              >
                <FaPlus />
              </Button>
            </InputGroup>

            <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
          </Form.Group>

          {/* Additional Emails */}
          {EXTRA_EMAIL_FIELDS.map((field, idx) => {
            if (extraEmails[field] === undefined) return null;

            return (
              <Form.Group key={field} className="mt-2">
                <InputGroup className="align-items-center">
                  <Form.Control
                    type="email"
                    value={extraEmails[field]}
                    placeholder={`Additional Email ${idx + 2}`}
                    onChange={(e) => handleExtraChange(field, e.target.value)}
                  />
                  <Button
                    variant="link"
                    className="text-danger ms-0 d-flex align-items-center"
                    onClick={() => handleRemoveExtraField(field)}
                    title="Remove"
                  >
                    <FaTrash />
                  </Button>
                </InputGroup>
              </Form.Group>
            );
          })}

          <p className="mt-2 ms-1">
            The invoice will be sent as a PDF attachment to all email addresses entered above.
            Emails added here are only used for sending and won’t be saved on the account.
            You will be notified once it has been sent.
          </p>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={sending}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={sending}>
          {sending ? "Sending..." : "Send"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
